<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="py-3 mx-4">
        <v-skeleton-loader
          v-if="loading"
          type="table"
          class="mb-4"
        ></v-skeleton-loader>

        <div v-else-if="!items || !items.length" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <div class="text-h6 text-h4 mt-2">沒有補休紀錄</div>
          <div class="text-body-2 text-h4">沒有資料可以顯示</div>
        </div>

        <template v-else>
          <template v-if="targetUserId">
            <!-- Summary Section -->
            <div class="mb-4">
              <v-row justify="center">
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-3 text-center">
                    <div class="text-subtitle-2 grey--text">剩餘補休</div>
                    <div class="text-caption grey--text">
                      ({{ totalRemainingMinutes }} 分鐘)
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <v-simple-table class="py-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">開始時間</th>
                    <th class="text-left">結束時間</th>
                    <th class="text-left">分鐘</th>
                    <th class="text-left">事由</th>
                    <th class="text-left">狀態</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in items" :key="item.id">
                    <td>{{ formatDateTime(item.start_time) }}</td>
                    <td>{{ formatDateTime(item.end_time) }}</td>
                    <td>{{ item.leave_minutes }}</td>
                    <td>{{ item.reason || "-" }}</td>
                    <td>
                      <v-chip
                        small
                        :color="getStatusColor(item.approval_status)"
                        text-color="white"
                      >
                        {{ item.approval_status }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>

          <template v-else>
            <v-simple-table class="py-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">員工</th>
                    <th class="text-left">手機</th>
                    <th class="text-left">剩餘補休(分鐘)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in items" :key="row.user_id">
                    <td>
                      {{ row.user && row.user.name ? row.user.name : "-" }}
                    </td>
                    <td>
                      {{
                        row.user && row.user.mobile_phone
                          ? row.user.mobile_phone
                          : "-"
                      }}
                    </td>
                    <td>{{ row.total_remaining_minutes }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>

          <div class="text-center my-4">
            <v-pagination
              v-model="page"
              :length="pageCount"
              @input="fetchCompensatoryLeaves"
              prev-icon="fa fa-angle-left"
              next-icon="fa fa-angle-right"
              color="#cb0c9f"
              circle
            ></v-pagination>
          </div>
        </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatDateTime } from "@/utils/time";

export default {
  name: "CompensatoryLeavesWidget",
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    targetUserId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      items: [],
      page: 1,
      itemsPerPage: 15,
      totalItems: 0,
      loading: false,
      fetchError: false,
      totalPages: 1,
      totalRemainingMinutes: 0,
    };
  },
  computed: {
    pageCount() {
      return this.totalPages || 1;
    },
  },
  async mounted() {
    await this.fetchCompensatoryLeaves();
  },
  watch: {
    selectedCompany: {
      handler() {
        this.fetchCompensatoryLeaves();
      },
      deep: true,
    },
  },
  methods: {
    async fetchCompensatoryLeaves() {
      this.loading = true;
      this.items = [];
      this.fetchError = false;

      try {
        const params = new URLSearchParams({
          page: String(this.page),
          limit: String(this.itemsPerPage),
        });
        let response;
        if (this.targetUserId) {
          response = await this.$axios.get(
            `/api/compensatory-leaves/sum/${
              this.targetUserId
            }?${params.toString()}`
          );
        } else {
          if (this.selectedCompany && this.selectedCompany.id) {
            params.append("company_id", String(this.selectedCompany.id));
          }
          response = await this.$axios.get(
            `/api/compensatory-leaves/sum-company?${params.toString()}`
          );
        }

        const payload = response && response.data ? response.data : {};
        const newItems = Array.isArray(payload.data) ? payload.data : [];
        const pagination = payload.pagination || {};

        if (this.targetUserId) {
          this.totalRemainingMinutes = payload.total_remaining_minutes || 0;
        }

        this.totalItems = Number(pagination.totalItems) || newItems.length || 0;
        this.totalPages = Number(pagination.totalPages) || 1;

        this.items = newItems;
      } catch (error) {
        this.items = [];
        this.totalItems = 0;
        this.fetchError = true;
        this.$emit("error", {
          title: "取得補休紀錄失敗",
          message:
            (error && (error.response?.data?.message || error.message)) ||
            "請稍後再試",
        });
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchCompensatoryLeaves();
    },
    formatDateTime(val) {
      return formatDateTime(val);
    },
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return "0";
      const days = minutes / 480; // Assuming 8 hours = 480 mins per day
      if (Number.isInteger(days)) {
        return days + " 天";
      }
      // Assuming maybe convert to hours if less than a day or mixed?
      // Or just keep decimal days
      return days.toFixed(2) + " 天";
    },
    getStatusColor(status) {
      if (status === "審核通過") return "success";
      if (status === "待審核") return "warning";
      if (status === "審核拒絕") return "error";
      return "grey";
    },
  },
};
</script>

<style scoped></style>
